import React, { useState, useCallback, ChangeEvent, FC } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import { Button, Form } from 'react-bootstrap';

interface AvatarCropProps {
  onCrop?: (croppedImageUrl: string) => void;
  onClose?: () => void;
  onBeforeFileLoad?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  srcImage?: string;
  label?: string;
  width?: number;
  height?: number;
}

const AvatarCrop: FC<AvatarCropProps> = ({
  onCrop,
  onClose,
  onBeforeFileLoad,
  srcImage,
  label = 'Choose File',
  width = 300,
  height = 195,
}) => {
  const [image, setImage] = useState<string | null>(srcImage || null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const aspectRatio = width / height;

  const onCropComplete = useCallback((_: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {return;}

    if (onBeforeFileLoad) {onBeforeFileLoad(e);}

    const reader = new FileReader();
    reader.onload = () => setImage(reader.result as string);
    reader.readAsDataURL(file);

  };

  const getCroppedImage = async () => {
    if (!image || !croppedAreaPixels) {return;}
    const croppedImg = await getCroppedImg(image, croppedAreaPixels);
    onCrop?.(croppedImg);
  };

  const getCroppedImg = (imageSrc: string, crop: Area): Promise<string> => {
    return new Promise((resolve) => {
      const image = new Image();
      image.src = imageSrc;
      image.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          return;
        }

        canvas.width = crop.width;
        canvas.height = crop.height;

        ctx.drawImage(
          image,
          crop.x,
          crop.y,
          crop.width,
          crop.height,
          0,
          0,
          crop.width,
          crop.height
        );

        const dataUrl = canvas.toDataURL('image/jpeg');
        resolve(dataUrl);
      };
    });
  };

  return (
    <div>
      {!image && (
        <Form.Group>
          <Form.Label>{label}</Form.Label>
          <Form.Control type="file" accept="image/*" onChange={handleFileChange} />
        </Form.Group>
      )}

      {image && (
        <>
          <div style={{ position: 'relative', width: '100%', height }}>
            <Cropper
              image={image}
              crop={crop}
              zoom={zoom}
              aspect={aspectRatio}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </div>

          <Form.Group className="mt-2">
            <Form.Label>Zoom</Form.Label>
            <Form.Range
              min={1}
              max={3}
              step={0.1}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
            />
          </Form.Group>

          <div className="d-flex gap-2 mt-3">
            <Button variant="primary" onClick={getCroppedImage}>
              Crop
            </Button>
            <Button variant="secondary" onClick={() => {
              setImage(null);
              onClose?.();
            }}>
              Cancel
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default AvatarCrop;
